import {
  MarketDataCardState,
  UnifiedLiquidityEnum,
  type MarketAccount,
  type MarketDataCardConfig,
  type MarketDataCardUnifiedLiquidityMarket,
  type Security,
  type UnifiedLiquidityResponse,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import type { MarketSelectorItem } from '../MarketSelector/types';
import { isMarketSelectorItemSelectable } from '../MarketSelector/utils/items';
import { isValidUnifiedLiquidityTokenSecurity } from '../MarketSelector/utils/unifiedLiquidity';
import { getOneAccountPerMarket } from '../OMS/utils';

export type MarketDataCardConfigAction =
  | {
      type: 'save';
      config: MarketDataCardConfig;
    }
  | {
      type: 'remove';
    }
  | undefined;

interface GetMarketDataCardConfigActionParams {
  config: MarketDataCardConfig;
  enableUnifiedLiquidityMarketSymbols?: boolean;
  items: MarketSelectorItem[];
  hasSecurity: boolean;
  marketAccountsByName?: ReadonlyMap<string, MarketAccount>;
  securitiesBySymbol?: ReadonlyMap<string, Security>;
  unifiedLiquidityTokenBySymbol?: ReadonlyMap<string, UnifiedLiquidityResponse>;
  unifiedLiquidityTokensLoaded?: boolean;
}

/**
 * Determines whether a market card with `initializeMarketAccounts` set should be updated or removed.
 *
 * When initialization is applicable, this will either:
 * - return a `save` action containing a config with one account per market and all standalone markets selected, or
 * - return a `remove` action if the symbol has no selectable markets/accounts.
 *
 * Returns `undefined` when no initialization work is needed.
 */
export function getMarketDataCardConfigAction({
  config,
  enableUnifiedLiquidityMarketSymbols = false,
  items,
  hasSecurity,
  marketAccountsByName,
  securitiesBySymbol,
  unifiedLiquidityTokenBySymbol,
  unifiedLiquidityTokensLoaded,
}: GetMarketDataCardConfigActionParams): MarketDataCardConfigAction {
  const shouldInitializeMarketAccounts =
    config.initializeMarketAccounts === true &&
    config.state !== MarketDataCardState.NEW_CARD &&
    config.symbol != null &&
    hasSecurity;
  if (!shouldInitializeMarketAccounts) {
    const configWithValidMarketSymbols = getConfigWithValidUnifiedLiquidityMarketSymbols({
      config,
      enableUnifiedLiquidityMarketSymbols,
      hasSecurity,
      marketAccountsByName,
      securitiesBySymbol,
      unifiedLiquidityTokenBySymbol,
      unifiedLiquidityTokensLoaded,
    });
    return isEqual(config, configWithValidMarketSymbols)
      ? undefined
      : { type: 'save', config: configWithValidMarketSymbols };
  }

  const initialSelections = getInitialSelections(items);
  const hasNoSelections =
    initialSelections.selectedMarkets.length === 0 && initialSelections.selectedMarketAccounts.length === 0;
  if (hasNoSelections) {
    return { type: 'remove' };
  }

  const newConfig: MarketDataCardConfig = {
    ...config,
    ...initialSelections,
    initializeMarketAccounts: false,
  };
  const configWithValidMarketSymbols = getConfigWithValidUnifiedLiquidityMarketSymbols({
    config: newConfig,
    enableUnifiedLiquidityMarketSymbols,
    hasSecurity,
    marketAccountsByName,
    securitiesBySymbol,
    unifiedLiquidityTokenBySymbol,
    unifiedLiquidityTokensLoaded,
  });

  return isEqual(config, configWithValidMarketSymbols)
    ? undefined
    : { type: 'save', config: configWithValidMarketSymbols };
}

function getInitialSelections(items: MarketSelectorItem[]) {
  const selectableItems = items.filter(isMarketSelectorItemSelectable);
  const selectedMarkets = selectableItems
    .filter((item): item is Extract<MarketSelectorItem, { type: 'Market' }> => item.type === 'Market')
    .map(item => item.name);
  const selectedMarketAccounts = getOneAccountPerMarket(
    selectableItems
      .filter((item): item is Extract<MarketSelectorItem, { type: 'MarketAccount' }> => item.type === 'MarketAccount')
      .map(item => item.marketAccount)
  ).map(marketAccount => marketAccount.Name);

  return {
    availableMarkets: selectedMarkets,
    selectedMarkets,
    availableMarketAccounts: selectedMarketAccounts,
    selectedMarketAccounts,
  };
}

// Migrates production UL configs and removes stale values from granular UL configs.
function getConfigWithValidUnifiedLiquidityMarketSymbols({
  config,
  enableUnifiedLiquidityMarketSymbols,
  hasSecurity,
  marketAccountsByName,
  securitiesBySymbol,
  unifiedLiquidityTokenBySymbol,
  unifiedLiquidityTokensLoaded,
}: Pick<
  GetMarketDataCardConfigActionParams,
  | 'config'
  | 'enableUnifiedLiquidityMarketSymbols'
  | 'hasSecurity'
  | 'marketAccountsByName'
  | 'securitiesBySymbol'
  | 'unifiedLiquidityTokenBySymbol'
  | 'unifiedLiquidityTokensLoaded'
>): MarketDataCardConfig {
  const shouldMigrateToUnifiedLiquidityMarketSymbols =
    enableUnifiedLiquidityMarketSymbols && config.unifiedLiquidityMarketSymbols === undefined;
  if (
    config.unifiedLiquidity !== UnifiedLiquidityEnum.Enabled ||
    config.symbol == null ||
    !hasSecurity ||
    (!shouldMigrateToUnifiedLiquidityMarketSymbols && !config.unifiedLiquidityMarketSymbols?.length) ||
    !unifiedLiquidityTokensLoaded ||
    marketAccountsByName == null ||
    securitiesBySymbol == null ||
    unifiedLiquidityTokenBySymbol == null
  ) {
    return config;
  }

  const availableSymbolsByMarket = getAvailableUnifiedLiquiditySymbolsByMarket({
    symbol: config.symbol,
    securitiesBySymbol,
    unifiedLiquidityTokenBySymbol,
  });

  if (availableSymbolsByMarket == null) {
    return config;
  }

  const unifiedLiquidityMarketSymbols = shouldMigrateToUnifiedLiquidityMarketSymbols
    ? getUnifiedLiquidityMarketSymbolsForSelectedAccounts({
        selectedMarketAccounts: config.selectedMarketAccounts,
        availableSymbolsByMarket,
        marketAccountsByName,
      })
    : getValidUnifiedLiquidityMarketSymbols({
        marketSymbols: config.unifiedLiquidityMarketSymbols ?? [],
        availableSymbolsByMarket,
        marketAccountsByName,
      });

  return isEqual(config.unifiedLiquidityMarketSymbols, unifiedLiquidityMarketSymbols)
    ? config
    : { ...config, unifiedLiquidityMarketSymbols };
}

function getAvailableUnifiedLiquiditySymbolsByMarket({
  symbol,
  securitiesBySymbol,
  unifiedLiquidityTokenBySymbol,
}: {
  symbol: string;
  securitiesBySymbol: ReadonlyMap<string, Security>;
  unifiedLiquidityTokenBySymbol: ReadonlyMap<string, UnifiedLiquidityResponse>;
}): Map<string, Set<string>> | undefined {
  const primarySecurity = securitiesBySymbol.get(symbol);
  const unifiedLiquidityTokens = unifiedLiquidityTokenBySymbol.get(symbol)?.Tokens;
  if (!isValidUnifiedLiquidityTokenSecurity(primarySecurity) || !unifiedLiquidityTokens?.length) {
    return new Map();
  }

  if (unifiedLiquidityTokens.some(token => securitiesBySymbol.get(token.Symbol) == null)) {
    return undefined;
  }

  const symbolsByMarket = new Map<string, Set<string>>();
  for (const market of primarySecurity.Markets ?? []) {
    symbolsByMarket.set(market, new Set([...(symbolsByMarket.get(market) ?? []), primarySecurity.Symbol]));
  }

  for (const token of unifiedLiquidityTokens) {
    if (!isValidUnifiedLiquidityTokenSecurity(securitiesBySymbol.get(token.Symbol))) {
      continue;
    }

    symbolsByMarket.set(token.Market, new Set([...(symbolsByMarket.get(token.Market) ?? []), token.Symbol]));
  }

  return symbolsByMarket;
}

function getUnifiedLiquidityMarketSymbolsForSelectedAccounts({
  selectedMarketAccounts,
  availableSymbolsByMarket,
  marketAccountsByName,
}: {
  selectedMarketAccounts: string[];
  availableSymbolsByMarket: ReadonlyMap<string, ReadonlySet<string>>;
  marketAccountsByName: ReadonlyMap<string, MarketAccount>;
}): MarketDataCardUnifiedLiquidityMarket[] {
  return selectedMarketAccounts.flatMap(marketAccount => {
    const market = marketAccountsByName.get(marketAccount)?.Market;
    return Array.from(market ? (availableSymbolsByMarket.get(market) ?? []) : [], symbol => ({
      marketAccount,
      symbol,
    }));
  });
}

function getValidUnifiedLiquidityMarketSymbols({
  marketSymbols,
  availableSymbolsByMarket,
  marketAccountsByName,
}: {
  marketSymbols: MarketDataCardUnifiedLiquidityMarket[];
  availableSymbolsByMarket: ReadonlyMap<string, ReadonlySet<string>>;
  marketAccountsByName: ReadonlyMap<string, MarketAccount>;
}): MarketDataCardUnifiedLiquidityMarket[] {
  return marketSymbols.filter(selection => {
    const market = marketAccountsByName.get(selection.marketAccount)?.Market;
    return market != null && availableSymbolsByMarket.get(market)?.has(selection.symbol);
  });
}
